import { useCallback, useRef, useState } from 'react';
import request from 'lib/request';
import type {
  EntityDocumentsResponse,
  EntityLinkedDocumentResponse,
} from 'types/api-responses';

// Lazily fetches the documents that cite an entity (POST
// /entities/:id/queries/documents) for the tearsheet table's row-expansion
// drilldown / the §3.13 View Documents table. Lazy because it's only needed once
// the table is shown; `page`/`page_size` are threaded to the endpoint (which
// paginates server-side) so the docs table's pager can walk pages, and
// `totalCount` is surfaced for the pager's total. Search + sort stay client-side
// over the loaded page (the drilldown is a peek, not the archive).
export function useEntityDocuments(entityId: number, page = 1, pageSize = 10) {
  const [documents, setDocuments] = useState<EntityLinkedDocumentResponse[]>(
    []
  );
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // A failed POST (network / server) is distinct from a genuinely empty result:
  // without this the component would render its empty-search copy for a failure.
  const [error, setError] = useState<string | null>(null);

  // Abort the prior in-flight page fetch and ignore any response that a newer
  // fetch has superseded, so paging can't resolve out of order (a slow page 1
  // landing after page 2). The request-id ref is the belt to the abort's braces:
  // an already-resolved-but-late response still fails the identity check.
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const fetchDocuments = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    const response = await request.post<
      EntityDocumentsResponse,
      { error: string }
    >(
      `/entities/${entityId}/queries/documents`,
      { page, page_size: pageSize },
      controller
    );
    // A superseded (or aborted) request must not clobber the current page.
    if (requestId !== requestIdRef.current || response.type === 'abort') return;
    if (response.type === 'success') {
      setDocuments(response.data.results);
      setTotalCount(response.data.totalCount);
      setLoaded(true);
    } else {
      setError(
        (response.type === 'error' && response.data?.error) ||
          'Documents could not be loaded. Try again.'
      );
    }
    setLoading(false);
  }, [entityId, page, pageSize]);

  return { documents, totalCount, loading, loaded, error, fetchDocuments };
}
